import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ujmsWorkflowRules, ujmsWorkflowCatalog, ujmsCreateWorkflowRule,
  ujmsUpdateWorkflowRule, ujmsDeleteWorkflowRule, ujmsWorkflowRuns,
  ujmsTicklers, ujmsPatchTickler, ujmsRunTicklers,
} from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, btnDanger, fmtDT, cls } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Zap, ArrowRight } from "lucide-react";

export function WorkflowTab() {
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    ujmsWorkflowRules().then((r) => setRules(r.data)).catch(() => {});
    ujmsWorkflowRuns({ limit: 30 }).then((r) => setRuns(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    try { await ujmsUpdateWorkflowRule(r.id, { enabled: !r.enabled }); toast.success(r.enabled ? "Rule disabled" : "Rule enabled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (r) => {
    try { await ujmsDeleteWorkflowRule(r.id); toast.success("Rule deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="workflow-tab">
      <Panel title="Automation rules — when an event happens, route a task to a department"
        testId="workflow-rules-panel"
        action={<button className={btnPrimary} onClick={() => setCreating(true)} data-testid="new-rule-btn"><Zap className="w-4 h-4" /> New Rule</button>}>
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Rule</th><th className={thCls}>Trigger Event</th><th className={thCls}></th>
            <th className={thCls}>Department</th><th className={thCls}>Task Title</th><th className={thCls}>Priority</th>
            <th className={thCls}>Due</th><th className={thCls}>Runs</th><th className={thCls}>Status</th><th className={thCls}></th>
          </tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`rule-row-${r.id}`}>
                <td className={tdCls}>{r.name}{r.system_defined && <span className="ml-1.5 text-[10px] font-mono text-slate-400">SYSTEM</span>}</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.trigger_event}</td>
                <td className={tdCls}><ArrowRight className="w-3.5 h-3.5 text-slate-400" /></td>
                <td className={`${tdCls} text-xs font-semibold`}>{r.assigned_role}</td>
                <td className={`${tdCls} text-xs`}>{r.title_template}</td>
                <td className={tdCls}><Badge value={r.priority} /></td>
                <td className={`${tdCls} font-mono text-xs`}>{r.due_days}d</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.run_count}</td>
                <td className={tdCls}>
                  <button onClick={() => toggle(r)} data-testid={`rule-toggle-${r.id}`}
                    className={cls("text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm border",
                      r.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-300")}>
                    {r.enabled ? "Enabled" : "Disabled"}
                  </button>
                </td>
                <td className={tdCls}><button className={btnDanger} onClick={() => remove(r)} data-testid={`rule-delete-${r.id}`}>Delete</button></td>
              </tr>
            ))}
            {!rules.length && <tr><td colSpan={10} className={tdCls}>No workflow rules configured.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <Panel title="Recent runs" testId="workflow-runs-panel">
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>When</th><th className={thCls}>Rule</th><th className={thCls}>Trigger</th>
            <th className={thCls}>Case</th><th className={thCls}>Task Created</th><th className={thCls}>Result</th>
          </tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} data-testid={`run-row-${r.id}`}>
                <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(r.created_at)}</td>
                <td className={`${tdCls} text-xs`}>{r.rule_name}</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.trigger_event}</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.case_number}</td>
                <td className={`${tdCls} text-xs`}>{r.task_title} <span className="text-slate-400">→ {r.assigned_role}</span></td>
                <td className={tdCls}><Badge value={r.status} /></td>
              </tr>
            ))}
            {!runs.length && <tr><td colSpan={6} className={tdCls}>No workflow executions yet — runs appear here as events fire.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <TicklersPanel />

      <RuleDialog open={creating} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />
    </div>
  );
}

function TicklersPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { ujmsTicklers().then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    try { await ujmsPatchTickler(r.code, { enabled: !r.enabled }); toast.success(r.enabled ? "Tickler disabled" : "Tickler enabled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setDays = async (r, days) => {
    try { await ujmsPatchTickler(r.code, { days: Number(days) }); toast.success(`${r.code} window set to ${days} day(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const run = async () => {
    setBusy(true);
    try {
      const r = await ujmsRunTicklers();
      const created = Object.entries(r.data.created || {}).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(created ? `Ticklers ran — created: ${created}` : "Ticklers ran — nothing due");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <Panel title="Ticklers — automatic deadline reminders (run nightly at 02:00)" testId="ticklers-panel"
      action={<button className={btnPrimary} disabled={busy} onClick={run} data-testid="ticklers-run-btn">
        {busy ? "Running…" : "Run Ticklers Now"}</button>}>
      <table className="w-full border-collapse">
        <thead><tr><th className={thCls}>Tickler</th><th className={thCls}>Watches for</th>
          <th className={thCls}>Window (days)</th><th className={thCls}>Task to</th>
          <th className={thCls}>Priority</th><th className={thCls}>Status</th></tr></thead>
        <tbody>
          {(data?.rules || []).map((r) => (
            <tr key={r.code} className="hover:bg-slate-50" data-testid={`tickler-row-${r.code}`}>
              <td className={`${tdCls} font-mono text-xs font-semibold`}>{r.code}</td>
              <td className={`${tdCls} text-xs`}>{r.description}</td>
              <td className={tdCls}>
                <input type="number" min="0" max="365" className={`${inputCls} w-20`} defaultValue={r.days}
                  onBlur={(e) => Number(e.target.value) !== r.days && setDays(r, e.target.value)}
                  data-testid={`tickler-days-${r.code}`} />
              </td>
              <td className={`${tdCls} text-xs font-semibold`}>{r.assigned_role}</td>
              <td className={tdCls}><Badge value={r.priority} /></td>
              <td className={tdCls}>
                <button onClick={() => toggle(r)} data-testid={`tickler-toggle-${r.code}`}
                  className={cls("text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm border",
                    r.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-300")}>
                  {r.enabled ? "Enabled" : "Disabled"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data?.last_run && (
        <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-200" data-testid="ticklers-last-run">
          Last run {fmtDT(data.last_run.created_at)} — {data.last_run.total_created} reminder task(s) created.
        </div>
      )}
    </Panel>
  );
}

function RuleDialog({ open, onClose, onDone }) {
  const [catalog, setCatalog] = useState(null);
  const [f, setF] = useState({ name: "", trigger_event: "", assigned_role: "", title_template: "", priority: "NORMAL", due_days: 3 });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) ujmsWorkflowCatalog().then((r) => setCatalog(r.data)).catch(() => {}); }, [open]);

  const submit = async () => {
    if (!f.name.trim() || !f.trigger_event || !f.assigned_role || !f.title_template.trim()) {
      toast.error("Name, trigger, department and task title are required"); return;
    }
    setBusy(true);
    try {
      await ujmsCreateWorkflowRule({ ...f, due_days: Number(f.due_days) });
      toast.success("Workflow rule created");
      setF({ name: "", trigger_event: "", assigned_role: "", title_template: "", priority: "NORMAL", due_days: 3 });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rule-dialog">
        <DialogHeader><DialogTitle>New Workflow Rule</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Rule name" required>
            <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="rule-name-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="When this event fires" required>
              <select className={selectCls} value={f.trigger_event} onChange={(e) => setF({ ...f, trigger_event: e.target.value })} data-testid="rule-event-select">
                <option value="">Select event…</option>
                {(catalog?.events || []).map((ev) => <option key={ev} value={ev}>{ev}</option>)}
              </select>
            </Field>
            <Field label="Create task for" required>
              <select className={selectCls} value={f.assigned_role} onChange={(e) => setF({ ...f, assigned_role: e.target.value })} data-testid="rule-role-select">
                <option value="">Select department…</option>
                {(catalog?.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Task title template" required>
            <input className={inputCls} value={f.title_template} onChange={(e) => setF({ ...f, title_template: e.target.value })}
              placeholder="e.g. Process ruling notice — {case_number}" data-testid="rule-template-input" />
            <div className="text-[11px] text-slate-400 mt-1">Placeholders: {"{case_number}"}, {"{event}"}, {"{actor}"}</div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority">
              <select className={selectCls} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="rule-priority-select">
                {(catalog?.priorities || ["LOW", "NORMAL", "HIGH", "URGENT"]).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Due in (days)">
              <input type="number" min="0" max="90" className={inputCls} value={f.due_days} onChange={(e) => setF({ ...f, due_days: e.target.value })} data-testid="rule-due-input" />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="rule-submit-btn">Create Rule</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
